import httpClient from "@/config/axios";
import type * as type from "@vTypes";
import type {
  SaveWeeklyOffNursePayload,
  WeeklyOffNurseList,
  WeeklyOffSettingConfig,
} from "@vTypes/weeklyOff";

export interface NurseMonthlyLimitItem {
  nurse_id: string;
  group_id: string;
  year: number;
  month: number;

  d_min?: number | null;
  d_max?: number | null;
  d_exact?: number | null;

  e_min?: number | null;
  e_max?: number | null;
  e_exact?: number | null;

  n_min?: number | null;
  n_max?: number | null;
  n_exact?: number | null;

  o_min?: number | null;
  o_max?: number | null;
  o_exact?: number | null;
}

export interface NurseMonthlyLimitWarning {
  code: string;
  message: string;
  severity: "warning";
}

export interface NurseMonthlyLimitMeta {
  target_nurse_count: number;
  active_nurse_count: number;
  override_ratio: number;
  recommended_ratio: number;
}

export interface NurseMonthlyLimitListResponse {
  items: NurseMonthlyLimitItem[];
  meta?: NurseMonthlyLimitMeta | null;
  warnings?: NurseMonthlyLimitWarning[] | null;
}

export interface NurseMonthlyLimitBulkUpsertRequest {
  year: number;
  month: number;
  limits: NurseMonthlyLimitItem[];
}

// 근무자관리 월 셀렉터 — 선택 월 '소속' 멤버 (Phase 4). 백엔드: GET /nurses/members
export type MembershipStatus =
  | "active"
  | "outbound"
  | "dispatch_out"
  | "leave"
  | "resigned"
  | "inbound";

export type MemberStatusReason = "퇴사" | "휴직" | "파견" | "병동이동" | null;

export interface GroupMemberInMonth {
  nurse_id: string;
  name: string | null;
  membership_status: MembershipStatus;
  marker: "←" | "→" | null; // 전출 ← / 전입 →
  badge: string | null; // "파견 중" | "휴직" | "퇴사" | "파견" | "N전담" | null
  as_of_team: number | null;
  as_of_grade: number | null;
  is_night_dedicated?: boolean; // N전담 배지 표시용 플래그(팀 배정엔 미사용 — 팀은 as_of_team)
  status_start_date?: string | null;
  status_end_date?: string | null;
  status_reason?: MemberStatusReason;
  resign_date?: string | null;
  resign_reason?: "퇴사" | null;
}

export interface GroupMembersResponse {
  members: GroupMemberInMonth[];
  headcount: { regular: number; moving: number; leave: number };
}

export interface NurseSequenceReorderRequest {
  active_order: string[];
  inactive_order: string[];
}

export interface NurseSequenceReorderResponse {
  message?: string;
}

// 모든 간호사 목록 조회 API
export const fetchFullNurse = async (
  groupId?: string
): Promise<type.FullNurseSchema[]> => {
  return httpClient.get<type.FullNurseSchema[]>("/nurses", {
    params: groupId ? { group_id: groupId } : undefined,
  });
};

// 근무자관리 월 셀렉터 — 선택 월 소속 명단 + 상태 + 헤드카운트
export const fetchGroupMembers = async (
  groupId: string,
  year: number,
  month: number
): Promise<GroupMembersResponse> => {
  return httpClient.get<GroupMembersResponse>("/nurses/members", {
    params: { group_id: groupId, year, month },
  });
};

export const reorderNurseSequence = async (
  payload: NurseSequenceReorderRequest,
  groupId?: string | null
): Promise<NurseSequenceReorderResponse> => {
  return httpClient.post<NurseSequenceReorderResponse>(
    "/nurses/sequence/reorder",
    payload,
    {
      params: groupId ? { group_id: groupId } : undefined,
    }
  );
};

// 주휴 설정 조회 API
export const weeklyOffSetting = async (
  groupId?: string
): Promise<WeeklyOffSettingConfig> => {
  return httpClient.get<WeeklyOffSettingConfig>("/weekly-off/settings", {
    params: groupId ? { group_id: groupId } : undefined,
  });
};

/**
 * 주휴 설정 저장 API
 * @param payload 저장할 주휴 설정 데이터
 * @param groupId (관리자용) 그룹 ID
 * @returns 저장된 주휴 설정 데이터
 */
export const saveWeeklyOffSetting = async (
  payload: Partial<WeeklyOffSettingConfig>,
  groupId?: string
): Promise<WeeklyOffSettingConfig> => {
  return httpClient.put<WeeklyOffSettingConfig>(
    "/weekly-off/settings",
    payload,
    {
      params: groupId ? { group_id: groupId } : undefined,
    }
  );
};

// 근무표 만들기 > 주휴 모달에서 사용 preview_weekday_label
// 간호사 주휴 설정 목록 및 미리보기 조회 API
export const weeklyOffNurses = async ({
  year,
  month,
  groupId,
}: {
  year: number;
  month: number;
  groupId?: string;
}): Promise<WeeklyOffNurseList> => {
  return httpClient.get<WeeklyOffNurseList>("/weekly-off/nurses", {
    params: { year, month, ...(groupId && { group_id: groupId }) },
  });
};

// 간호사별 주휴 설정 저장 API (저장 시점의 연/월이 기준 시점으로 갱신됨)
export const saveWeeklyOffNurses = async (
  payload: SaveWeeklyOffNursePayload[],
  groupId?: string
): Promise<WeeklyOffNurseList> => {
  return httpClient.put<WeeklyOffNurseList>(
    "/weekly-off/nurses",
    { items: payload },
    {
      params: groupId ? { group_id: groupId } : undefined,
    }
  );
};
